/*
 * Scentt sections \u2013 small custom elements shared by the homepage & content sections.
 *   <scentt-countdown data-end="2026-12-24 18:00" data-tz="+0000" data-expired="\u2026" hidden>  promo banner
 *   <scentt-autoslide data-interval="5">  auto-slides the .scentt-rail inside it, only while it is on screen
 *   <scentt-fit data-max="8">  shrinks the font of a one-line list on phones until every item fits (trust bar)
 */
(() => {
  const S = window.Scentt && Object.isFrozen(window.Scentt) && typeof window.Scentt.html === 'function' ? window.Scentt : null;
  const int = (v, d, min, max) => (S ? S.int(v, d, min, max) : d);
  const reduced = () => (S ? S.reducedMotion : false);

  /* ---------- countdown ---------- */
  const pad = (n) => String(n).padStart(2, '0');
  const parseEnd = (rawValue, tzValue) => {
    let raw = String(rawValue || '').trim().slice(0, 32).replace(' ', 'T');
    const tz = String(tzValue || '');
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) raw += 'T23:59';
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(raw)) raw += ':00';
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}([zZ]|[+-]\d{2}:?\d{2})?$/.test(raw)) return NaN;
    if (/^[+-]\d{4}$/.test(tz) && !/([zZ]|[+-]\d{2}:?\d{2})$/.test(raw)) raw += `${tz.slice(0, 3)}:${tz.slice(3)}`;
    return new Date(raw).getTime();
  };

  class ScenttCountdown extends HTMLElement {
    connectedCallback() {
      this.end = parseEnd(this.dataset.end, this.dataset.tz);
      if (Number.isNaN(this.end)) return;
      this.units = {};
      this.querySelectorAll('[data-unit]').forEach((u) => { this.units[u.getAttribute('data-unit')] = u; });
      this.hidden = false;
      if (this.tick()) this.timer = setInterval(() => { if (!this.tick()) clearInterval(this.timer); }, 1000);
    }

    disconnectedCallback() { clearInterval(this.timer); }

    tick() {
      const left = Math.max(0, Math.floor((this.end - Date.now()) / 1000));
      const set = (k, v) => { if (this.units[k]) this.units[k].textContent = pad(v); };
      set('d', Math.min(99, Math.floor(left / 86400)));
      set('h', Math.floor((left % 86400) / 3600));
      set('m', Math.floor((left % 3600) / 60));
      set('s', left % 60);
      if (left > 0) return true;
      this.classList.add('is-ended');
      const label = this.querySelector('[data-countdown-label]');
      if (label) label.textContent = String(this.dataset.expired || '').slice(0, 200);
      return false;
    }
  }
  if (!customElements.get('scentt-countdown')) customElements.define('scentt-countdown', ScenttCountdown);

  /* ---------- auto-slide (testimonials): every width, only while in view, pauses on interaction ---------- */
  class ScenttAutoslide extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('.scentt-rail');
      this.secs = int(this.dataset.interval, 0, 0, 30);
      if (!this.rail || !this.secs || reduced()) return;
      this.visible = false;
      this.holdUntil = 0;
      this.onHold = () => { this.holdUntil = Date.now() + 4000; };
      this.onEnter = () => { this.hovering = true; };
      this.onLeave = () => { this.hovering = false; this.onHold(); };
      ['pointerdown', 'touchstart', 'focusin', 'wheel'].forEach((ev) => this.rail.addEventListener(ev, this.onHold, { passive: true }));
      this.addEventListener('mouseenter', this.onEnter);
      this.addEventListener('mouseleave', this.onLeave);
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => { this.visible = entries.some((en) => en.isIntersecting); this.toggle(); }, { threshold: 0.3 });
        this.io.observe(this);
      } else {
        this.visible = true;
        this.toggle();
      }
      this.onVisibility = () => this.toggle();
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.timer = null;
      if (this.io) this.io.disconnect();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }

    /* The timer only exists while the slider is on screen and the tab is visible. */
    toggle() {
      const run = this.visible && !document.hidden && this.isConnected;
      this.classList.toggle('is-running', run);
      if (run && !this.timer) this.timer = setInterval(() => this.step(), this.secs * 1000);
      if (!run && this.timer) { clearInterval(this.timer); this.timer = null; }
    }

    step() {
      if (this.hovering || Date.now() < this.holdUntil) return;
      const r = this.rail;
      const max = r.scrollWidth - r.clientWidth;
      if (max < 4) return;
      const first = r.firstElementChild;
      const second = first && first.nextElementSibling;
      const stepPx = second ? second.offsetLeft - first.offsetLeft : r.clientWidth;
      const left = r.scrollLeft >= max - 2 ? 0 : Math.min(max, r.scrollLeft + stepPx);
      r.scrollTo({ left, behavior: 'smooth' });
      this.dispatchEvent(new CustomEvent('scentt:autoslide', { bubbles: true, detail: { left } }));
    }
  }
  if (!customElements.get('scentt-autoslide')) customElements.define('scentt-autoslide', ScenttAutoslide);

  /* ---------- fit one line (trust bar on phones) ---------- */
  class ScenttFit extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('ul');
      if (!this.list) return;
      this.onResize = S ? S.debounce(() => this.fit(), 120) : () => this.fit();
      window.addEventListener('resize', this.onResize, { passive: true });
      this.fit();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.isConnected && this.fit());
    }

    disconnectedCallback() { if (this.onResize) window.removeEventListener('resize', this.onResize); }

    fit() {
      const ul = this.list;
      ul.style.removeProperty('--trust-fs-fit');
      ul.style.removeProperty('--trust-ls-fit');
      ul.classList.remove('is-overflow');
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      let size = int(this.dataset.max, 8, 6, 14);
      ul.style.setProperty('--trust-fs-fit', `${size}px`);
      if (ul.scrollWidth <= ul.clientWidth) return;
      ul.style.setProperty('--trust-ls-fit', '0.02em');
      /* read, then write; bounded loop (max 40 steps of 0.2px) */
      for (let i = 0; i < 40 && ul.scrollWidth > ul.clientWidth && size > 6; i += 1) {
        size = Math.round((size - 0.2) * 10) / 10;
        ul.style.setProperty('--trust-fs-fit', `${size}px`);
      }
      /* Still too long at the smallest size: let it scroll instead of cutting text off */
      if (ul.scrollWidth > ul.clientWidth) ul.classList.add('is-overflow');
    }
  }
  if (!customElements.get('scentt-fit')) customElements.define('scentt-fit', ScenttFit);
})();
